import React, { useState } from 'react';
import {
  FileText,
  Plus,
  Search,
  Filter,
  Eye,
  CheckCircle2,
  Printer,
  Shield,
  User,
  Calendar,
  Layers,
  ArrowRight,
  Download,
  Lock
} from 'lucide-react';
import { ConsultingAstrologerBasicReport, NatalPromiseTier } from '../../types/consultingReport';
import { UserProfile, UserRole } from '../../types/user';
import { Language, CompleteKundali } from '../../types/kundali';
import { formatDate } from '../../utils/dateUtils';
import { getConsultingReports, saveConsultingReport } from '../../lib/consultingReportService';

interface ConsultingReportingCentreProps {
  currentUser: UserProfile | null;
  lang: Language;
  kundali?: CompleteKundali;
}

const NATAL_PROMISE_TIERS: NatalPromiseTier[] = [
  'Exceptional',
  'Highly Supportive',
  'Moderate/Balanced',
  'Challenging/Requires Effort',
  'Constrained'
];

export const ConsultingReportingCentre: React.FC<ConsultingReportingCentreProps> = ({
  currentUser,
  lang,
  kundali
}) => {
  const isHi = lang === 'hi';
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;
  const isManagement = currentUser?.role === UserRole.ADMIN || (currentUser as any)?.activeRole === 'MANAGEMENT';
  const isAstrologer = currentUser?.role === UserRole.CONSULTING_ASTROLOGER;

  const [reports, setReports] = useState<ConsultingAstrologerBasicReport[]>(() => getConsultingReports(currentUser));
  const [viewMode, setViewMode] = useState<'my' | 'all' | 'selected'>(isAstrologer ? 'my' : 'all');
  const [selectedAstrologerFilter, setSelectedAstrologerFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [themeFilter, setThemeFilter] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Report Builder Modal state
  const [isBuildingReport, setIsBuildingReport] = useState<boolean>(false);
  const [previewReport, setPreviewReport] = useState<ConsultingAstrologerBasicReport | null>(null);

  // Form state for new/edit report
  const [consultationId, setConsultationId] = useState<string>('cns_001');
  const [clientRef, setClientRef] = useState<string>('CLI-8821');
  const [clientName, setClientName] = useState<string>(kundali?.birthDetails?.name || 'Client Native');
  const [consultedTheme, setConsultedTheme] = useState<string>('Career & Professional Growth');
  const [consultedQuestion, setConsultedQuestion] = useState<string>('Promotion timing and Saturn mahadasha alignment.');
  const [natalPromiseTier, setNatalPromiseTier] = useState<NatalPromiseTier>('Highly Supportive');
  const [primaryTimingWindow, setPrimaryTimingWindow] = useState<string>('October 2026 – March 2027');
  const [supportingFactors, setSupportingFactors] = useState<string>('Jupiter in 10th house aspecting 2nd lord; Exalted Sun in D9.');
  const [guidance, setGuidance] = useState<string>('Proceed with major professional transitions during waxing Moon fortnights.');
  const [remedies, setRemedies] = useState<string>('Yellow Sapphire (Pukhraj) on right index finger; Daily Surya Arghya.');
  const [consultationNotes, setConsultationNotes] = useState<string>('Client expressed strong intent for relocation. Chart indicates favourable foreign travel yoga in 9th house.');
  const [followUpStatus, setFollowUpStatus] = useState<'Pending' | 'Scheduled' | 'Completed' | 'Not Required'>('Scheduled');

  const handleCreateReport = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    const newReport = saveConsultingReport(currentUser, {
      consultationId,
      consultantAstrologerId: currentUser.uid,
      consultantAstrologerName: currentUser.displayName || currentUser.email || 'Consulting Astrologer',
      clientReferenceId: clientRef,
      clientDisplayName: clientName,
      consultationDate: new Date().toISOString().split('T')[0],
      consultationStatus: 'active',
      consultedTheme,
      consultedQuestion,
      natalPromiseTier,
      primaryTimingWindow,
      supportingAstrologicalFactors: supportingFactors.split(';').map((s) => s.trim()).filter(Boolean),
      relevantGuidance: guidance,
      relevantRemedies: remedies.split(';').map((s) => s.trim()).filter(Boolean),
      consultationNotes,
      followUpStatus
    });

    setReports(getConsultingReports(currentUser));
    setIsBuildingReport(false);
  };

  // Filter logic
  const filteredReports = reports.filter((r) => {
    const matchSearch =
      r.clientDisplayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.clientReferenceId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.consultationId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.consultedTheme.toLowerCase().includes(searchTerm.toLowerCase());

    const matchStatus = statusFilter === 'ALL' || r.consultationStatus === statusFilter;
    const matchTheme = themeFilter === 'ALL' || r.consultedTheme === themeFilter;

    let matchView = true;
    if (viewMode === 'my') {
      matchView = r.consultantAstrologerId === currentUser?.uid;
    } else if (viewMode === 'selected' && selectedAstrologerFilter !== 'ALL') {
      matchView = r.consultantAstrologerId === selectedAstrologerFilter;
    }

    return matchSearch && matchStatus && matchTheme && matchView;
  });

  // Unique astrologers for selection filter
  const uniqueAstrologers = Array.from(new Set(reports.map((r) => r.consultantAstrologerId)));
  const uniqueThemes = Array.from(new Set(reports.map((r) => r.consultedTheme)));

  return (
    <div className="space-y-6">
      {/* Header & Sub-Nav */}
      <div className="bg-astro-bg p-5 rounded-2xl border border-astro-border shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-2 rounded-xl bg-astro-primary text-white">
              <FileText className="w-5 h-5" />
            </span>
            <div>
              <h2 className="text-lg font-serif font-bold text-astro-text">
                {isHi ? 'परामर्श ज्योतिषी बेसिक रिपोर्टिंग केंद्र' : 'Consulting Astrologer Basic Reporting Centre'}
              </h2>
              <p className="text-xs text-astro-secondary-text">
                {isHi
                  ? 'व्यावहारिक ज्योतिषीय परामर्श dossiers, Natal Promise टियर्स और अनुवर्ती सिंथेटिक रिकॉर्ड्स।'
                  : 'Practice-level practitioner dossiers, qualitative Natal Promise tiers & clinical tracking.'}
              </p>
            </div>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2">
          {(isAstrologer || isSuperAdmin) && (
            <button
              onClick={() => setIsBuildingReport(true)}
              className="min-h-[40px] px-4 py-2 rounded-xl bg-astro-primary hover:bg-astro-secondary text-white text-xs font-bold flex items-center gap-2 shadow-xs transition-all cursor-pointer"
            >
              <Plus className="w-4 h-4 text-astro-accent" />
              <span>{isHi ? '+ नई बेसिक रिपोर्ट बनाएं' : '+ Create Basic Report'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Reporting Hierarchy Tabs (My vs All vs Selected) */}
      <div className="flex flex-wrap items-center gap-2 border-b border-astro-border pb-3">
        {isAstrologer && (
          <button
            onClick={() => setViewMode('my')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              viewMode === 'my'
                ? 'bg-astro-primary text-white shadow-xs'
                : 'bg-white border border-astro-border text-astro-secondary-text hover:bg-astro-card'
            }`}
          >
            {isHi ? 'मेरी बेसिक रिपोर्ट्स (My Reports)' : 'My Astrologer Basic Reports'}
          </button>
        )}

        {(isSuperAdmin || isManagement || isAstrologer) && (
          <button
            onClick={() => setViewMode('all')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              viewMode === 'all'
                ? 'bg-astro-primary text-white shadow-xs'
                : 'bg-white border border-astro-border text-astro-secondary-text hover:bg-astro-card'
            }`}
          >
            {isHi ? 'सभी ज्योतिषी रिपोर्ट्स (All Reports)' : 'All Astrologer Reports'}
          </button>
        )}

        {(isSuperAdmin || isManagement) && (
          <button
            onClick={() => setViewMode('selected')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              viewMode === 'selected'
                ? 'bg-astro-primary text-white shadow-xs'
                : 'bg-white border border-astro-border text-astro-secondary-text hover:bg-astro-card'
            }`}
          >
            {isHi ? 'चयनित ज्योतिषी रिपोर्ट्स (Selected Astrologer)' : 'Selected Astrologer Reports'}
          </button>
        )}
      </div>

      {/* Consultation Filters Toolbar */}
      <div className="bg-white p-4 rounded-xl border border-astro-border shadow-xs grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-astro-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={isHi ? 'क्लाइंट, आईडी या थीम खोजें...' : 'Search client, ID, theme...'}
            className="w-full pl-9 pr-3 py-2 text-xs bg-astro-card/50 rounded-lg border border-astro-border text-astro-text focus:outline-none focus:ring-2 focus:ring-astro-primary"
          />
        </div>

        {viewMode === 'selected' && (
          <select
            value={selectedAstrologerFilter}
            onChange={(e) => setSelectedAstrologerFilter(e.target.value)}
            className="px-3 py-2 text-xs bg-astro-card/50 rounded-lg border border-astro-border text-astro-text focus:outline-none focus:ring-2 focus:ring-astro-primary"
          >
            <option value="ALL">{isHi ? 'सभी ज्योतिषी (All Astrologers)' : 'All Astrologers'}</option>
            {uniqueAstrologers.map((astId) => (
              <option key={astId} value={astId}>
                Astrologer ID: {astId.substring(0, 10)}...
              </option>
            ))}
          </select>
        )}

        <select
          value={themeFilter}
          onChange={(e) => setThemeFilter(e.target.value)}
          className="px-3 py-2 text-xs bg-astro-card/50 rounded-lg border border-astro-border text-astro-text focus:outline-none focus:ring-2 focus:ring-astro-primary"
        >
          <option value="ALL">{isHi ? 'सभी परामर्श थीम (All Themes)' : 'All Consultation Themes'}</option>
          {uniqueThemes.map((th) => (
            <option key={th} value={th}>
              {th}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-xs bg-astro-card/50 rounded-lg border border-astro-border text-astro-text focus:outline-none focus:ring-2 focus:ring-astro-primary"
        >
          <option value="ALL">{isHi ? 'सभी स्थितियाँ (All Statuses)' : 'All Statuses'}</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="revoked">Revoked</option>
        </select>
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredReports.map((report) => (
          <div
            key={report.reportId}
            className="bg-astro-bg p-5 rounded-2xl border border-astro-border shadow-xs hover:shadow-md transition-all flex flex-col justify-between gap-4"
          >
            <div>
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-[11px] font-mono font-bold text-astro-accent bg-astro-accent/10 px-2.5 py-0.5 rounded border border-astro-accent/20">
                  {report.consultationId}
                </span>
                <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-800">
                  {report.natalPromiseTier}
                </span>
              </div>

              <h3 className="font-serif font-bold text-astro-text text-sm">
                {report.clientDisplayName} ({report.clientReferenceId})
              </h3>
              <p className="text-xs font-semibold text-astro-primary pt-1">
                Theme: {report.consultedTheme}
              </p>
              <p className="text-[11px] text-astro-secondary-text line-clamp-2 italic pt-1">
                "{report.consultedQuestion}"
              </p>
            </div>

            <div className="space-y-3 pt-3 border-t border-astro-border">
              <div className="text-[11px] text-astro-secondary-text space-y-1">
                <p><strong>Astrologer:</strong> {report.consultantAstrologerName}</p>
                <p><strong>Timing Window:</strong> {report.primaryTimingWindow}</p>
                <p><strong>Follow-Up:</strong> <span className="text-astro-accent font-bold">{report.followUpStatus}</span></p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPreviewReport(report)}
                  className="flex-1 min-h-[36px] px-3 py-1.5 rounded-xl bg-astro-primary hover:bg-astro-secondary text-white text-xs font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-xs"
                >
                  <Eye className="w-3.5 h-3.5 text-astro-accent" />
                  <span>{isHi ? 'रिपोर्ट पूर्वावलोकन' : 'View Report'}</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredReports.length === 0 && (
        <div className="p-12 text-center bg-astro-bg rounded-2xl border border-astro-border text-xs text-astro-secondary-text">
          {isHi ? 'कोई रिपोर्ट नहीं मिली।' : 'No Consulting Astrologer Basic Reports found matching filter criteria.'}
        </div>
      )}

      {/* Create Report Modal */}
      {isBuildingReport && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-astro-bg rounded-2xl border border-astro-border shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto space-y-4">
            <div className="flex items-center justify-between border-b border-astro-border pb-3">
              <h3 className="font-serif font-bold text-base text-astro-text">
                {isHi ? 'नया कंसल्टिंग ज्योतिषी बेसिक रिपोर्ट बनाएं' : 'Create Consulting Astrologer Basic Report'}
              </h3>
              <button
                onClick={() => setIsBuildingReport(false)}
                className="text-xs text-astro-secondary-text hover:text-astro-text font-bold"
              >
                ✕ Close
              </button>
            </div>

            <form onSubmit={handleCreateReport} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-astro-secondary-text mb-1">Consultation ID</label>
                  <input
                    type="text"
                    value={consultationId}
                    onChange={(e) => setConsultationId(e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-white rounded-lg border border-astro-border text-astro-text"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-astro-secondary-text mb-1">Client Reference & Name</label>
                  <input
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-white rounded-lg border border-astro-border text-astro-text"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-astro-secondary-text mb-1">Consulted Theme</label>
                  <input
                    type="text"
                    value={consultedTheme}
                    onChange={(e) => setConsultedTheme(e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-white rounded-lg border border-astro-border text-astro-text"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-astro-secondary-text mb-1">Natal Promise Tier (Qualitative)</label>
                  <select
                    value={natalPromiseTier}
                    onChange={(e) => setNatalPromiseTier(e.target.value as NatalPromiseTier)}
                    className="w-full px-3 py-2 text-xs bg-white rounded-lg border border-astro-border text-astro-text"
                  >
                    {NATAL_PROMISE_TIERS.map((tier) => (
                      <option key={tier} value={tier}>{tier}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-astro-secondary-text mb-1">Consulted Question / Focus</label>
                <input
                  type="text"
                  value={consultedQuestion}
                  onChange={(e) => setConsultedQuestion(e.target.value)}
                  className="w-full px-3 py-2 text-xs bg-white rounded-lg border border-astro-border text-astro-text"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-astro-secondary-text mb-1">Primary Timing Window</label>
                <input
                  type="text"
                  value={primaryTimingWindow}
                  onChange={(e) => setPrimaryTimingWindow(e.target.value)}
                  className="w-full px-3 py-2 text-xs bg-white rounded-lg border border-astro-border text-astro-text"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-astro-secondary-text mb-1">Key Supporting Astrological Factors (separated by semicolon)</label>
                <textarea
                  value={supportingFactors}
                  onChange={(e) => setSupportingFactors(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-xs bg-white rounded-lg border border-astro-border text-astro-text"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-astro-secondary-text mb-1">Relevant Guidance</label>
                  <textarea
                    value={guidance}
                    onChange={(e) => setGuidance(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 text-xs bg-white rounded-lg border border-astro-border text-astro-text"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-astro-secondary-text mb-1">Relevant Remedies (separated by semicolon)</label>
                  <textarea
                    value={remedies}
                    onChange={(e) => setRemedies(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 text-xs bg-white rounded-lg border border-astro-border text-astro-text"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-astro-secondary-text mb-1">Consultation Confidential Notes</label>
                <textarea
                  value={consultationNotes}
                  onChange={(e) => setConsultationNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-xs bg-white rounded-lg border border-astro-border text-astro-text"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-astro-secondary-text mb-1">Follow-Up Status</label>
                <select
                  value={followUpStatus}
                  onChange={(e) => setFollowUpStatus(e.target.value as any)}
                  className="w-full px-3 py-2 text-xs bg-white rounded-lg border border-astro-border text-astro-text"
                >
                  <option value="Scheduled">Scheduled</option>
                  <option value="Pending">Pending</option>
                  <option value="Completed">Completed</option>
                  <option value="Not Required">Not Required</option>
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-astro-border">
                <button
                  type="button"
                  onClick={() => setIsBuildingReport(false)}
                  className="px-4 py-2 rounded-xl bg-astro-secondary-surface text-astro-secondary-text text-xs font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl bg-astro-primary hover:bg-astro-secondary text-white text-xs font-bold shadow-xs"
                >
                  Save Basic Report
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Report Preview Modal */}
      {previewReport && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-astro-bg rounded-2xl border border-astro-border shadow-2xl max-w-3xl w-full p-8 max-h-[90vh] overflow-y-auto space-y-6">
            <div className="flex items-center justify-between border-b border-astro-border pb-4">
              <div>
                <span className="text-[10px] font-mono font-bold text-astro-accent bg-astro-accent/10 px-2.5 py-0.5 rounded border border-astro-accent/30">
                  {previewReport.reportId}
                </span>
                <h3 className="font-serif font-bold text-lg text-astro-text mt-1">
                  CONSULTING ASTROLOGER BASIC REPORT
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => window.print()}
                  className="min-h-[36px] px-3 py-1.5 rounded-xl bg-astro-primary text-white text-xs font-bold flex items-center gap-1.5 shadow-xs cursor-pointer"
                >
                  <Printer className="w-4 h-4" />
                  <span>Print / PDF</span>
                </button>
                <button
                  onClick={() => setPreviewReport(null)}
                  className="px-3 py-1.5 rounded-xl bg-astro-card text-astro-secondary-text text-xs font-bold"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="space-y-4 text-xs text-astro-text">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-4 rounded-xl bg-astro-card border border-astro-border">
                <div>
                  <p className="text-[10px] text-astro-secondary-text">Consulting Astrologer</p>
                  <p className="font-bold">{previewReport.consultantAstrologerName}</p>
                </div>
                <div>
                  <p className="text-[10px] text-astro-secondary-text">Consultation ID</p>
                  <p className="font-mono font-bold">{previewReport.consultationId}</p>
                </div>
                <div>
                  <p className="text-[10px] text-astro-secondary-text">Client Reference</p>
                  <p className="font-bold">{previewReport.clientDisplayName} ({previewReport.clientReferenceId})</p>
                </div>
                <div>
                  <p className="text-[10px] text-astro-secondary-text">Consultation Date</p>
                  <p>{formatDate(previewReport.consultationDate)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-astro-secondary-text">Natal Promise Tier</p>
                  <p className="font-bold text-astro-accent">{previewReport.natalPromiseTier}</p>
                </div>
                <div>
                  <p className="text-[10px] text-astro-secondary-text">Follow-Up Status</p>
                  <p className="font-bold">{previewReport.followUpStatus}</p>
                </div>
              </div>

              <div>
                <h4 className="font-serif font-bold text-sm text-astro-primary mb-1">Consultation Theme & Question</h4>
                <p className="p-3 bg-white rounded-xl border border-astro-border">
                  <strong>Theme:</strong> {previewReport.consultedTheme}<br />
                  <strong>Question:</strong> "{previewReport.consultedQuestion}"
                </p>
              </div>

              <div>
                <h4 className="font-serif font-bold text-sm text-astro-primary mb-1">Primary Timing Window</h4>
                <p className="p-3 bg-white rounded-xl border border-astro-border font-semibold text-astro-accent">
                  {previewReport.primaryTimingWindow}
                </p>
              </div>

              <div>
                <h4 className="font-serif font-bold text-sm text-astro-primary mb-1">Key Supporting Astrological Factors</h4>
                <ul className="list-disc pl-5 space-y-1 p-3 bg-white rounded-xl border border-astro-border">
                  {previewReport.supportingAstrologicalFactors.map((fact, idx) => (
                    <li key={idx}>{fact}</li>
                  ))}
                </ul>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-serif font-bold text-sm text-astro-primary mb-1">Relevant Guidance</h4>
                  <p className="p-3 bg-white rounded-xl border border-astro-border">{previewReport.relevantGuidance}</p>
                </div>
                <div>
                  <h4 className="font-serif font-bold text-sm text-astro-primary mb-1">Relevant Remedies</h4>
                  <ul className="list-disc pl-5 space-y-1 p-3 bg-white rounded-xl border border-astro-border">
                    {previewReport.relevantRemedies.map((rem, idx) => (
                      <li key={idx}>{rem}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div>
                <h4 className="font-serif font-bold text-sm text-astro-primary mb-1">Consultation Notes</h4>
                <p className="p-3 bg-white rounded-xl border border-astro-border italic">{previewReport.consultationNotes}</p>
              </div>

              <div className="pt-4 border-t border-astro-border text-[10px] text-astro-secondary-text flex justify-between">
                <span>Integrity Hash: <code className="font-mono">{previewReport.integrityHash}</code></span>
                <span>Generated: {formatDate(previewReport.createdAt)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
